import { useEffect, useRef } from 'react';

/**
 * Hook to prevent text selection from being cleared when the mouse leaves
 * the editor element during a drag selection operation.
 *
 * This is a workaround for a browser limitation where contentEditable elements
 * lose their selection when the mouse leaves the element boundary during drag.
 *
 * @param elementRef - Ref to the contentEditable element
 * @param enabled - Whether the hook should be active (typically tied to editable state)
 */
export const usePreventSelectionClearance = (
  elementRef: React.RefObject<HTMLElement>,
  enabled: boolean = true,
) => {
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const editorElement = elementRef.current;
    if (!editorElement) return;

    let savedSelection: Range | null = null;
    let restoreTimeout: number | null = null;

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 0) {
        isDraggingRef.current = true;
        savedSelection = null;
      }
    };

    const handleMouseMove = () => {
      if (isDraggingRef.current) {
        // Save current selection during drag
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          // Only save non-collapsed selections
          if (!range.collapsed) {
            savedSelection = range.cloneRange();
          }
        }
      }
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      savedSelection = null;
      if (restoreTimeout !== null) {
        clearTimeout(restoreTimeout);
        restoreTimeout = null;
      }
    };

    // Prevent mouseleave from clearing selection during drag
    const handleMouseLeave = (e: MouseEvent) => {
      if (isDraggingRef.current && e.buttons === 1 && savedSelection) {
        // Maintain focus to help preserve selection
        if (document.activeElement !== editorElement) {
          editorElement.focus();
        }
      }
    };

    // Monitor selection changes to restore selection if cleared during drag
    const handleSelectionChange = () => {
      if (isDraggingRef.current && savedSelection) {
        const selection = window.getSelection();
        // If selection was cleared or collapsed but we're still dragging, restore it
        if (selection && (selection.rangeCount === 0 || selection.isCollapsed)) {
          // Use a small timeout to avoid interfering with ongoing selection
          if (restoreTimeout !== null) {
            clearTimeout(restoreTimeout);
          }
          restoreTimeout = window.setTimeout(() => {
            if (isDraggingRef.current && savedSelection) {
              const currentSelection = window.getSelection();
              if (currentSelection) {
                try {
                  // Check if selection is still cleared before restoring
                  if (currentSelection.rangeCount === 0 || currentSelection.isCollapsed) {
                    currentSelection.removeAllRanges();
                    currentSelection.addRange(savedSelection);
                  }
                } catch (err) {
                  // Selection might be invalid, ignore
                }
              }
            }
          }, 10);
        } else if (selection && selection.rangeCount > 0) {
          // Update saved selection if it's still valid
          const range = selection.getRangeAt(0);
          if (!range.collapsed && editorElement.contains(range.commonAncestorContainer)) {
            savedSelection = range.cloneRange();
          }
        }
      }
    };

    editorElement.addEventListener('mousedown', handleMouseDown);
    editorElement.addEventListener('mousemove', handleMouseMove);
    editorElement.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('selectionchange', handleSelectionChange);

    return () => {
      editorElement.removeEventListener('mousedown', handleMouseDown);
      editorElement.removeEventListener('mousemove', handleMouseMove);
      editorElement.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('selectionchange', handleSelectionChange);
      if (restoreTimeout !== null) {
        clearTimeout(restoreTimeout);
      }
    };
  }, [enabled, elementRef]);
};
